import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { conferences } from '../api';
import Navbar from '../components/Navbar';

const ConferenceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new' || id === 'undefined';

  const [form, setForm] = useState({
    title: '',
    date: '',
    end_date: '',
    location: '',
    organizer: '',
    organizer_contact: '',
    show_timer: false,
    timer_warning_minutes: 10,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      conferences.getOne(id)
        .then((res) => {
          const c = res.data;
          setForm({
            title: c.title,
            date: c.date.split('T')[0],
            end_date: c.end_date ? c.end_date.split('T')[0] : '',
            location: c.location || '',
            organizer: c.organizer || '',
            organizer_contact: c.organizer_contact || '',
            show_timer: c.show_timer,
            timer_warning_minutes: c.timer_warning_minutes,
          });
        })
        .catch(() => setError('Kunne ikke hente konferanse'));
    }
  }, [id, isNew]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isNew) {
        const res = await conferences.create(form);
        navigate(`/conference/${res.data.id}`);
      } else {
        await conferences.update(id, form);
        navigate(`/conference/${id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-dark-200">
      <Navbar title={isNew ? 'Ny konferanse' : 'Rediger konferanse'} backTo="/" />

      <div className="max-w-2xl mx-auto p-6">
        <div className="card dark:bg-dark-50 dark:border-dark-100">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-6 text-sm border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Tittel *</label>
              <input type="text" name="title" value={form.title} onChange={handleChange}
                className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100"
                placeholder="Årskonferanse 2026" required />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Startdato *</label>
                <input type="date" name="date" value={form.date} onChange={handleChange}
                  className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100" required />
              </div>
              <div>
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Sluttdato</label>
                <input type="date" name="end_date" value={form.end_date} onChange={handleChange}
                  min={form.date}
                  className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100" />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Sted</label>
              <input type="text" name="location" value={form.location} onChange={handleChange}
                className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100"
                placeholder="Oslo Kongressenter" />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Arrangør</label>
                <input type="text" name="organizer" value={form.organizer} onChange={handleChange}
                  className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100"
                  placeholder="Navn på organisasjon" />
              </div>
              <div>
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">Kontaktperson</label>
                <input type="text" name="organizer_contact" value={form.organizer_contact} onChange={handleChange}
                  className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100"
                  placeholder="Navn Navnesen" />
              </div>
            </div>

            <div className="mb-5 flex items-center gap-3 p-4 bg-cream-50 dark:bg-dark-100 rounded-lg">
              <input type="checkbox" name="show_timer" id="show_timer" checked={form.show_timer}
                onChange={handleChange} className="w-4 h-4 accent-navy-700" />
              <label htmlFor="show_timer" className="text-navy-700 dark:text-cream-200 text-sm font-medium">
                Vis nedtellingstimer under presentasjoner
              </label>
            </div>

            {form.show_timer && (
              <div className="mb-5">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-medium mb-1">
                  Varsle når det er X minutter igjen
                </label>
                <input type="number" name="timer_warning_minutes" value={form.timer_warning_minutes}
                  onChange={handleChange} min="1" max="60"
                  className="input dark:bg-dark-100 dark:border-dark-50 dark:text-cream-100" />
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
                {loading ? 'Lagrer...' : 'Lagre'}
              </button>
              <button type="button" onClick={() => navigate('/')} className="btn-secondary">
                Avbryt
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConferenceForm;
